/**
 * 韶音报价费用项模板
 * 按业务类型和费用分类组织，支持预定义费用项选择
 */

/**
 * 预付/到付枚举
 */
export const PrepaidCollectEnum = {
  PREPAID: { value: 1, label: '预付' },
  COLLECT: { value: 2, label: '到付' }
} as const

/** 韶音供应商报价基础运费编码。 */
export const SHOKZ_REQUIRED_FEE_CODES = {
  SEA_BASE_FREIGHT: 'GB0001',
  AIR_BASE_FREIGHT: 'GB5013',
  PRE_PORT_BASE_FREIGHT: 'Pre_Basic',
  LAST_MILE_BASE_FREIGHT: 'Lms_Basic',
  LEGACY_SEA_BASE_FREIGHT: 'OCEAN_FREIGHT',
  LEGACY_AIR_BASE_FREIGHT: 'AIR_FREIGHT_0_299'
} as const

/**
 * 费用项基础结构
 */
export interface FeeItemTemplate {
  /** 费用编码（唯一标识） */
  code: string
  /** 历史费用编码，仅用于兼容已有报价，不用于新费用回填 */
  legacyCodes?: string[]
  /** 中文名称 */
  name: string
  /** 英文名称 */
  nameEn: string
  /** 是否必填：boolean 或 条件函数 */
  required: boolean | ((context: FeeRequiredContext) => boolean)
  /** 非 All In 必填规则是否依赖目的国家资料 */
  requiresDestinationCountry?: boolean
  /** 默认计费单位代码 */
  defaultMeasureUnit?: string
  /** 说明 */
  description?: string
  /** 分类：origin-起运港 freight-运费 destination-目的港 */
  category: 'origin' | 'freight' | 'destination'
}

/**
 * 必填校验上下文
 */
export interface FeeRequiredContext {
  /** 是否北美航线 */
  isNorthAmerica: boolean
  /** 是否日本航线 */
  isJapan: boolean
  /** 是否需要拆柜 */
  needTransload: boolean
  /** 是否需要报关 */
  needCustoms: boolean
  /** 是否启用 SHOKZ 非 All In 费用规则；未提供时沿用原模板规则 */
  isNonAllInShokzQuotation?: boolean
}

// ==================== 海运整箱(FCL)费用项模板 ====================

/**
 * 起运港费用（Origin Charge）
 */
export const FCL_ORIGIN_CHARGE_FEES: FeeItemTemplate[] = [
  {
    code: 'PICKUP_FEE',
    name: '提货费',
    nameEn: 'Pickup Fee',
    required: true,
    defaultMeasureUnit: 'CNTR',
    description: '起运港必填费用',
    category: 'origin'
  },
  {
    code: 'Handling_POL',
    name: '操作费',
    nameEn: 'Handling',
    required: true,
    defaultMeasureUnit: 'BILL',
    description: '起运港操作费',
    category: 'origin'
  },
  {
    code: 'DOCUMENT',
    name: '文件费',
    nameEn: 'Document',
    required: true,
    defaultMeasureUnit: 'BILL',
    description: '起运港文件费',
    category: 'origin'
  },
  {
    code: 'OTHC_POL',
    name: '起运港码头操作费',
    nameEn: 'OTHC',
    required: true,
    defaultMeasureUnit: 'CNTR',
    description: 'OTHC/THC',
    category: 'origin'
  },
  {
    code: 'EIR',
    name: 'EIR打单费',
    nameEn: 'EIR',
    required: false,
    defaultMeasureUnit: 'BILL',
    category: 'origin'
  },
  {
    code: 'SEAL',
    name: '封条费',
    nameEn: 'Seal',
    required: false,
    defaultMeasureUnit: 'CTNR',
    category: 'origin'
  },
  {
    code: 'VGM',
    name: 'VGM申报费',
    nameEn: 'VGM',
    required: false,
    defaultMeasureUnit: 'BILL',
    description: '不含磅重',
    category: 'origin'
  },
  {
    code: 'EXPORT_CUSTOM',
    name: '出口报关费',
    nameEn: 'Export Custom',
    required: (ctx) => ctx.needCustoms,
    defaultMeasureUnit: 'ENTRY',
    description: '如需报关',
    category: 'origin'
  },
  {
    code: 'EXTRA_PAGE',
    name: '出口报关续页费',
    nameEn: 'Extra Page',
    required: false,
    defaultMeasureUnit: 'PAGE',
    category: 'origin'
  },
  {
    code: 'MANIFEST',
    name: '申报费（AMS/ENS/CFR）',
    nameEn: 'Manifest',
    required: false,
    defaultMeasureUnit: 'BILL',
    description: '根据目的国填写对应申报费',
    category: 'origin'
  }
]

/**
 * 海运费（Basic Freight）
 */
export const FCL_FREIGHT_FEES: FeeItemTemplate[] = [
  {
    code: SHOKZ_REQUIRED_FEE_CODES.SEA_BASE_FREIGHT,
    legacyCodes: [SHOKZ_REQUIRED_FEE_CODES.LEGACY_SEA_BASE_FREIGHT],
    name: '海运费',
    nameEn: 'Ocean Freight',
    required: true,
    defaultMeasureUnit: 'CNTR',
    category: 'freight'
  },
  {
    code: 'OCEAN_FREIGHT_LCL',
    name: '海运费（拼柜）',
    nameEn: 'Ocean Freight LCL',
    required: false,
    defaultMeasureUnit: 'CBM',
    description: '含拼柜报价时填写',
    category: 'freight'
  },
  {
    code: 'PSS',
    name: '旺季附加费',
    nameEn: 'PSS',
    required: false,
    defaultMeasureUnit: 'TEU',
    description: '如有请注明收费时间区间',
    category: 'freight'
  },
  {
    code: 'OTHERS_1',
    name: '其他附加费',
    nameEn: 'Others-1',
    required: false,
    description: '填写费用名称+收费标准说明',
    category: 'freight'
  }
]

/**
 * 目的港费用（Destination Charge）
 */
export const FCL_DESTINATION_CHARGE_FEES: FeeItemTemplate[] = [
  {
    code: 'OTHC_POD',
    name: '目的港码头操作费',
    nameEn: 'DTHC',
    required: true,
    defaultMeasureUnit: 'CNTR',
    category: 'destination'
  },
  {
    code: 'IMPORT_CLEARANCE',
    name: '进口清关费',
    nameEn: 'Import Clearance',
    required: true,
    defaultMeasureUnit: 'ENTRY',
    description: '进口报关费',
    category: 'destination'
  },
  {
    code: 'DO_FEE',
    name: '换单费',
    nameEn: 'D/O Fee',
    required: true,
    defaultMeasureUnit: 'BILL',
    category: 'destination'
  },
  {
    code: 'CTLF',
    name: '拆柜费',
    nameEn: 'Container Trans-load Fee',
    required: (ctx) => ctx.needTransload,
    defaultMeasureUnit: 'CNTR',
    description: '需拆柜派送时必填；含短拖及仓库操作费',
    category: 'destination'
  },
  {
    code: 'DELIVERY_CHARGE',
    name: '配送费',
    nameEn: 'Delivery Charge',
    required: true,
    defaultMeasureUnit: 'CNTR',
    description: '不需拆柜时为整柜派送费；需拆柜时为整车配送费',
    category: 'destination'
  },
  {
    code: 'PRE_PULL',
    name: '预提费',
    nameEn: 'Pre-pull',
    required: false,
    defaultMeasureUnit: 'CTNR',
    description: '需预提柜时报价',
    category: 'destination'
  },
  {
    code: 'CHASSIS_FEE',
    name: '车架费',
    nameEn: 'Chassis Fee',
    required: false,
    defaultMeasureUnit: 'CTNR',
    category: 'destination'
  },
  {
    code: 'CHASSIS_SPLIT',
    name: '车架分离费',
    nameEn: 'Chassis Split',
    required: false,
    defaultMeasureUnit: 'CTNR',
    category: 'destination'
  },
  {
    code: 'OTHERS_2',
    name: '其他目的港杂费',
    nameEn: 'Others-2',
    required: false,
    description: '填写费用类型及收费标准',
    category: 'destination'
  }
]

/**
 * 海运整箱全部费用项（按分类组织）
 */
export const FCL_FEE_TEMPLATE = {
  origin: FCL_ORIGIN_CHARGE_FEES,
  freight: FCL_FREIGHT_FEES,
  destination: FCL_DESTINATION_CHARGE_FEES
}

// ==================== 港前/尾程费用项模板 ====================

/**
 * 港前/尾程固定费用
 */
export const PREPORT_LASTMILE_FIXED_FEES: FeeItemTemplate[] = [
  {
    code: 'PICKUP_FEE',
    name: '提货费',
    nameEn: 'Pickup Fee',
    required: true,
    defaultMeasureUnit: 'CNTR',
    category: 'origin'
  },
  {
    code: 'Handling_POL',
    name: '操作费',
    nameEn: 'Handling',
    required: true,
    defaultMeasureUnit: 'BILL',
    category: 'origin'
  },
  {
    code: 'DOCUMENT',
    name: '文件费',
    nameEn: 'Document',
    required: true,
    defaultMeasureUnit: 'BILL',
    category: 'origin'
  }
]

/** 港前基础费用，用于独立港前及主运输港前分段。 */
export const PREPORT_BASE_FEE: FeeItemTemplate = {
  code: SHOKZ_REQUIRED_FEE_CODES.PRE_PORT_BASE_FREIGHT,
  name: '港前基础费用',
  nameEn: 'Pre-port Basic Fee',
  required: true,
  category: 'freight'
}

/** 尾程基础费用，用于独立尾程及主运输尾程分段。 */
export const LASTMILE_BASE_FEE: FeeItemTemplate = {
  code: SHOKZ_REQUIRED_FEE_CODES.LAST_MILE_BASE_FREIGHT,
  name: '尾程基础费用',
  nameEn: 'Last-mile Basic Fee',
  required: true,
  category: 'freight'
}

/**
 * 港前/尾程潜在费用
 */
export const PREPORT_LASTMILE_POTENTIAL_FEES: FeeItemTemplate[] = [
  {
    code: 'CUSTOMS_FEE',
    name: '报关费',
    nameEn: 'Customs Fee',
    required: false,
    defaultMeasureUnit: 'ENTRY',
    category: 'origin'
  },
  {
    code: 'STORAGE_FEE',
    name: '仓储费',
    nameEn: 'Storage Fee',
    required: false,
    defaultMeasureUnit: 'DAY',
    category: 'origin'
  },
  {
    code: 'OTHERS',
    name: '其他费用',
    nameEn: 'Others',
    required: false,
    category: 'origin'
  }
]

/**
 * 港前费用模板
 */
export const PREPORT_FEE_TEMPLATE = {
  fixed: [PREPORT_BASE_FEE, ...PREPORT_LASTMILE_FIXED_FEES],
  potential: PREPORT_LASTMILE_POTENTIAL_FEES
}

/**
 * 尾程费用模板
 */
export const LASTMILE_FEE_TEMPLATE = {
  fixed: [LASTMILE_BASE_FEE, ...PREPORT_LASTMILE_FIXED_FEES],
  potential: PREPORT_LASTMILE_POTENTIAL_FEES
}

// ==================== 空运费用项模板 ====================

/**
 * 空运起运港费用
 */
export const AIR_ORIGIN_CHARGE_FEES: FeeItemTemplate[] = [
  {
    code: 'PICKUP_FEE',
    name: '提货费',
    nameEn: 'Pickup Fee',
    required: true,
    defaultMeasureUnit: 'KG',
    description: '需提货时报价，填单价；另报 MIN / MAX',
    category: 'origin'
  },
  {
    code: 'Handling_POL',
    name: '操作费',
    nameEn: 'Handling',
    required: ctx => ctx.isNonAllInShokzQuotation ? ctx.isNorthAmerica || ctx.isJapan : true,
    requiresDestinationCountry: true,
    defaultMeasureUnit: 'BILL',
    description: '起运港操作费',
    category: 'origin'
  },
  {
    code: 'DOCUMENT',
    name: '文件费',
    nameEn: 'Document',
    required: ctx => ctx.isNonAllInShokzQuotation ? ctx.isNorthAmerica || ctx.isJapan : true,
    requiresDestinationCountry: true,
    defaultMeasureUnit: 'BILL',
    category: 'origin'
  },
  {
    code: 'THC_POL',
    name: '机场杂费',
    nameEn: 'Airport Fee',
    required: true,
    defaultMeasureUnit: 'KG',
    description: '起运港机场杂费；另报 MIN / MAX',
    category: 'origin'
  },
  {
    code: 'EXPORT_CUSTOM_AIR',
    name: '出口报关费',
    nameEn: 'Export Custom',
    required: false,
    defaultMeasureUnit: 'ENTRY',
    description: '如需报关时填写',
    category: 'origin'
  },
  {
    code: 'EXTRA_PAGE_AIR',
    name: '出口报关续页费用',
    nameEn: 'Extra Page',
    required: false,
    defaultMeasureUnit: 'LINE',
    category: 'origin'
  }
]

/**
 * 空运费（按重量段）
 */
export const AIR_FREIGHT_FEES: FeeItemTemplate[] = [
  {
    code: SHOKZ_REQUIRED_FEE_CODES.AIR_BASE_FREIGHT,
    legacyCodes: [SHOKZ_REQUIRED_FEE_CODES.LEGACY_AIR_BASE_FREIGHT],
    name: '空运费 (0-299KGS)',
    nameEn: 'Air Freight 0-299KGS',
    required: true,
    defaultMeasureUnit: 'KG',
    description: '该重量段单价（USD/kg）',
    category: 'freight'
  },
  {
    code: 'AIR_FREIGHT_300_499',
    name: '空运费 (300-499KGS)',
    nameEn: 'Air Freight 300-499KGS',
    required: true,
    defaultMeasureUnit: 'KG',
    category: 'freight'
  },
  {
    code: 'AIR_FREIGHT_500_999',
    name: '空运费 (500-999KGS)',
    nameEn: 'Air Freight 500-999KGS',
    required: true,
    defaultMeasureUnit: 'KG',
    category: 'freight'
  },
  {
    code: 'AIR_FREIGHT_1000_UP',
    name: '空运费 (≥1000KGS)',
    nameEn: 'Air Freight ≥1000KGS',
    required: true,
    defaultMeasureUnit: 'KG',
    category: 'freight'
  }
]

/**
 * 空运目的港费用
 */
export const AIR_DESTINATION_CHARGE_FEES: FeeItemTemplate[] = [
  {
    code: 'THC_POD',
    name: '目的机场杂费',
    nameEn: 'Dest. Airport Fee',
    required: true,
    defaultMeasureUnit: 'KG',
    description: '目的机场杂费；另报 MIN / MAX',
    category: 'destination'
  },
  {
    code: 'Handling_POD',
    name: '操作费',
    nameEn: 'Handling Fee',
    required: true,
    defaultMeasureUnit: 'BILL',
    description: '目的港操作费',
    category: 'destination'
  },
  {
    code: 'DO_FEE',
    name: 'D/O费',
    nameEn: 'D/O Fee',
    required: ctx => !ctx.isNonAllInShokzQuotation,
    defaultMeasureUnit: 'BILL',
    description: '换单费',
    category: 'destination'
  },
  {
    code: 'IMPORT_CLEARANCE',
    name: '进口清关费',
    nameEn: 'Import Clearance',
    required: true,
    defaultMeasureUnit: 'ENTRY',
    category: 'destination'
  },
  {
    code: 'CUSTOM_T1',
    name: 'T1转关费',
    nameEn: 'Custom T1',
    required: ctx => !ctx.isNonAllInShokzQuotation,
    defaultMeasureUnit: 'BILL',
    description: '如需T1转关时填写，欧盟必填',
    category: 'destination'
  },
  {
    code: 'DELIVERY_CHARGE',
    name: '配送费',
    nameEn: 'Delivery Charge',
    required: true,
    defaultMeasureUnit: 'KG',
    description: 'Door类服务时必填；另报 MIN / MAX',
    category: 'destination'
  },
  {
    code: 'CFS_HANDLING',
    name: 'CFS转仓费',
    nameEn: 'CFS Handling Fee',
    required: ctx => ctx.isNonAllInShokzQuotation ? !(ctx.isNorthAmerica || ctx.isJapan) : true,
    requiresDestinationCountry: true,
    defaultMeasureUnit: 'KG',
    description: '机场货站提到CFS仓库费用(如有)',
    category: 'destination'
  },
  {
    code: 'CFS_STORAGE',
    name: 'CFS仓库超期存储费',
    nameEn: 'CFS Storage Fee',
    required: ctx => ctx.isNonAllInShokzQuotation ? !(ctx.isNorthAmerica || ctx.isJapan) : true,
    requiresDestinationCountry: true,
    defaultMeasureUnit: 'DAY_KG',
    description: 'CFS超期存储的费用(如有)',
    category: 'destination'
  },
  {
    code: 'AIRPORT_STORAGE',
    name: '机场货站超期存储费',
    nameEn: 'Airport Storage Fee',
    required: ctx => ctx.isNonAllInShokzQuotation ? !(ctx.isNorthAmerica || ctx.isJapan) : true,
    requiresDestinationCountry: true,
    defaultMeasureUnit: 'DAY_KG',
    description: '超免存期后产生，目的地机场超期存储费，可报AT COST，凭票收费',
    category: 'destination'
  },
  {
    code: 'PALLETIZE',
    name: '打板费',
    nameEn: 'Palletize Handling Fee',
    required: ctx => !ctx.isNonAllInShokzQuotation && (ctx.isNorthAmerica || ctx.isJapan),
    defaultMeasureUnit: 'PLTS',
    description: '需要打板操作时填写',
    category: 'destination'
  },
  {
    code: 'PALLET_FEE',
    name: '托盘材料费',
    nameEn: 'Pallet Fee',
    required: ctx => !ctx.isNonAllInShokzQuotation && (ctx.isNorthAmerica || ctx.isJapan),
    defaultMeasureUnit: 'PLTS',
    description: '需要托盘材料时填写',
    category: 'destination'
  },
  {
    code: 'OTHERS_AIR',
    name: '其他费用',
    nameEn: 'Others-2',
    required: false,
    description: '填写费用类型及收费标准',
    category: 'destination'
  }
]

/**
 * 空运全部费用项
 */
export const AIR_FEE_TEMPLATE = {
  origin: AIR_ORIGIN_CHARGE_FEES,
  freight: AIR_FREIGHT_FEES,
  destination: AIR_DESTINATION_CHARGE_FEES
}

// ==================== 工具函数 ====================

/**
 * 根据业务类型获取费用模板
 * @param businessType fcl | lcl | air | prePort | lastMile
 */
export function getFeeTemplate(businessType: 'fcl' | 'lcl' | 'air' | 'prePort' | 'lastMile') {
  switch (businessType) {
    case 'fcl':
    case 'lcl':
      return FCL_FEE_TEMPLATE
    case 'air':
      return AIR_FEE_TEMPLATE
    case 'prePort':
      return PREPORT_FEE_TEMPLATE
    case 'lastMile':
      return LASTMILE_FEE_TEMPLATE
    default:
      return FCL_FEE_TEMPLATE
  }
}

/**
 * 获取所有可选费用项（扁平化列表，用于下拉选择）
 * @param businessType 业务类型
 */
export function getAllFeeOptions(businessType: 'fcl' | 'lcl' | 'air' | 'prePort' | 'lastMile') {
  const template = getFeeTemplate(businessType)
  const fees: FeeItemTemplate[] = []

  if ('origin' in template) {
    fees.push(...template.origin)
    fees.push(...template.freight)
    fees.push(...template.destination)
  } else if ('fixed' in template) {
    fees.push(...template.fixed)
    fees.push(...template.potential)
  }

  return fees.map(fee => ({
    value: fee.code,
    label: fee.name,
    nameEn: fee.nameEn,
    category: fee.category,
    description: fee.description
  }))
}

/**
 * 根据费用编码获取费用模板信息
 */
export function getFeeByCode(code: string, businessType: 'fcl' | 'lcl' | 'air' | 'prePort' | 'lastMile'): FeeItemTemplate | undefined {
  const template = getFeeTemplate(businessType)
  const normalizedCode = String(code || '').trim().toUpperCase()
  const matchesCode = (fee: FeeItemTemplate) => fee.code.toUpperCase() === normalizedCode ||
    (fee.legacyCodes || []).some(legacyCode => legacyCode.toUpperCase() === normalizedCode)

  if ('origin' in template) {
    return [...template.origin, ...template.freight, ...template.destination]
      .find(matchesCode) || [PREPORT_BASE_FEE, LASTMILE_BASE_FEE].find(matchesCode)
  } else if ('fixed' in template) {
    return [...template.fixed, ...template.potential]
      .find(matchesCode)
  }

  return undefined
}
